import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DriverSection from "../../components/reserve/DriverSection";
import ExtrasSection from "../../components/reserve/ExtrasSection";
import LocationSection from "../../components/reserve/LocationSection";
import ScheduleSection from "../../components/reserve/ScheduleSection";
import Stepper from "../../components/reserve/Stepper";
import SummaryPanel from "../../components/reserve/SummaryPanel";
import VehicleSection from "../../components/reserve/VehicleSection";
import moment from "moment";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { errorAlert } from "../../components/common/Alert";
import { AuthContext } from "../../states/AuthContext";
import { singleSlotBooking, getSessionStatus, multipleSlotBooking } from "../../services/Networkcall";
import Swal from "sweetalert2";
import PaymentModal from "./PaymentModal";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";
import { Loader } from "../../components/common/Loader";
import ExtendParking from "../../components/reserve/ExtendParking";
import InputField from "../../components/common/InputField";
import { Button } from "react-bootstrap";

export default function ReserveBooking() {

  const location = useLocation()
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [inData, setInData] = useState({ startTime: moment().format("hh:mm A"), endTime: "11:59 PM" });
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedBookingType, setSelectedBookingType] = useState("DAY");
  const [addOnServicesList, setAddOnServicesList] = useState([]);
  const [isMonthlySubscription, setIsMonthlySubscription] = useState(true);
  const [loading, setLoading] = useState(false);
  const [stripeSessionDetail, setStripeSessionDetail] = useState(null);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("STRIPE");
  const [confirmSchedule, setConfirmSchedule] = useState(false);
  const [confirmAddOn, setConfirmAddOn] = useState(false);
  const [confirmSpots, setConfirmSpots] = useState(false);
  const [extendBookingData, setExtendBookingData] = useState({ location: "", bookingId: '', bookingDates: [] });
  const [searchParams] = useSearchParams();
  const [remainingTime, setRemainingTime] = useState(
    () => parseInt(localStorage.getItem("remainingTime"), 10) || 300
  );
  const [isExtendParking, setIsExtendParking] = useState(false);
  const [bookingMode, setBookingMode] = useState("multiple");
  const [numberOfSpots, setNumberOfSpots] = useState(1);
  const [selectedMultipleDrivers, setMultipleDrivers] = useState([])
  const [selectedMultipleVehicles, setMultipleVehicles] = useState([]);
  const editBookingData = location?.state?.bookingData;
  const editMode = location?.state?.editMode;


  useEffect(() => {
    const isExtend = searchParams.get("extend") === "true";
    if (isExtend) {
      const data = location?.state?.data?.selectedBooking;
      if (data) {
        setSelectedDriver(data?.driver);
        setSelectedVehicle(data?.vehicle);
        setExtendBookingData((prev) => ({ ...prev, location: data?.location?._id, bookingDates: data?.reservation, bookingId: data?._id }));
        setIsExtendParking(true);
        setIsMonthlySubscription(true);
      }
    }
  }, []);



  useEffect(() => {
    if (editMode && editBookingData) {
      if (bookingMode === "multiple") {
        setMultipleDrivers(editBookingData?.drivers || []);
        setMultipleVehicles(editBookingData?.vehicles || []);
      } else {
        setSelectedDriver(
          editBookingData?.driver_info || editBookingData?.driver || null
        );

        setSelectedVehicle(
          editBookingData?.vehicle_info || editBookingData?.vehicle || null
        );

        setSelectedLocation(
          editBookingData?.location_info || editBookingData?.location || null
        );

        setSelectedDates(editBookingData?.booking_dates || []);

        setAddOnServicesList(
          (editBookingData?.addOnServices || editBookingData?.add_on_services || [])
            .map(service => ({
              ...service,
              isSelected: true
            }))
        );

        setConfirmSchedule(true);
        setConfirmAddOn(true);

        setCollapsed({
          driver: true,
          vehicle: true,
          location: true,
          schedule: false,
          extras: true,
        });
      }
    }
  }, [editMode, editBookingData, bookingMode]);



  const { userProfileData } = useContext(AuthContext);
  const navigate = useNavigate();

  // ─── COLLAPSE STATE ─────────────────────────────────────────
  // false = expanded (default), true = collapsed
  const [collapsed, setCollapsed] = useState({
    driver: false,
    vehicle: false,
    location: false,
    schedule: false,
    extras: false,
  });

  const [multiCollapsed, setMultiCollapsed] = useState({
    location: false,
    spots: false,
    plan: false,
    driver: false,
    vehicle: false,
    schedule: false,
    extras: false,
  });

  const toggleCollapse = (section) => {
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
    if (section === 'schedule' && confirmSchedule) { setConfirmSchedule(false) };
    if (section === 'extras' && confirmAddOn) { setConfirmAddOn(false) };
  };

  // ─── SCROLL REFS ─────────────────────────────────────────────
  const vehicleRef = useRef(null);
  const driverRef = useRef(null);
  const locationRef = useRef(null);
  const scheduleRef = useRef(null);
  const spotsRef = useRef(null);
  const extrasRef = useRef(null);
  const summaryRef = useRef(null);

  const scrollToRef = (ref) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  // ================================================
  // SECTION HANDLERS WITH AUTO COLLAPSE  
  // ================================================

  // Driver selected → collapse driver → show vehicle
  const handleDriverSelect = (driver) => {
    setSelectedDriver(driver);
    if (driver?._id) {
      setCollapsed((prev) => ({ ...prev, driver: true }));
      scrollToRef(vehicleRef);
    }
  };

  // Vehicle selected → collapse vehicle → show location
  const handleVehicleSelect = (vehicle) => {

    if (bookingMode === "multiple") {
      const exists = selectedMultipleVehicles.find(v => v._id === vehicle._id);
      let updatedVehicles;
      if (exists) {
        updatedVehicles = selectedMultipleVehicles.filter(v => v._id !== vehicle._id);
      } else {
        updatedVehicles = [...selectedMultipleVehicles, vehicle];
      }
      setMultipleVehicles(updatedVehicles);

      if (updatedVehicles.length > 0) {
        setCollapsed((prev) => ({ ...prev, vehicle: true }));
        scrollToRef(locationRef);
      }
    } else {
      setSelectedVehicle(vehicle);

      if (vehicle?._id) {
        setCollapsed((prev) => ({ ...prev, vehicle: true }));
        scrollToRef(locationRef);
      }
    }
  };

  // Location selected → collapse location → show schedule
  const handleLocationSelect = useCallback((location) => {
    if (editMode) return;

    setSelectedLocation(location);

    if (location?._id) {
      setCollapsed((prev) => ({ ...prev, location: true, schedule: true }));
      scrollToRef(scheduleRef);
    }
    if (bookingMode === "multiple") {
      setMultiCollapsed((p) => ({ ...p, location: true }));
      scrollToRef(spotsRef);
    }
    
  }, [editMode]);

  // Dates selected → collapse schedule → scroll to extras
  useEffect(() => {
    if (!editMode && confirmSchedule) {
      setCollapsed((prev) => ({ ...prev, schedule: false, extras: false }));
      scrollToRef(extrasRef);
    }
    if (!editMode && confirmAddOn) {
      setCollapsed((prev) => ({ ...prev, extras: true }));
    }
  }, [confirmSchedule, confirmAddOn]);


  // ─── STEPPER ─────────────────────────────────────────────────
  const getStepStatus = (isDone, isActive) => {
    if (isDone) return "done";
    if (isActive) return "active";
    return "pending";
  };

  const steps = useMemo(() => [
    { label: "Driver", status: getStepStatus(!!selectedDriver?._id, !selectedDriver?._id) },
    { label: "Vehicle", status: getStepStatus(!!selectedVehicle?._id, !!selectedDriver?._id && !selectedVehicle?._id) },
    { label: "Select Location", status: getStepStatus(!!selectedLocation?._id, !!selectedVehicle?._id && !selectedLocation?._id) },
    { label: "Schedule", status: getStepStatus(selectedDates.length > 0, !!selectedLocation?._id && selectedDates.length === 0) },
    { label: "Add On Services", status: getStepStatus(selectedDates.length > 0 && addOnServicesList.length > 0, selectedDates.length > 0 && addOnServicesList.length === 0) },
  ], [selectedDriver, selectedVehicle, selectedLocation, selectedDates, addOnServicesList]);

  const multipleSteps = useMemo(() => [
    {
      label: "Location",
      status: getStepStatus(!!selectedLocation?._id, !selectedLocation?._id)
    },
    {
      label: "Spots",
      status: getStepStatus(multiCollapsed?.spots, !!selectedLocation?._id)
    },
    {
      label: "Schedule",
      status: getStepStatus(confirmSchedule, selectedDates.length > 0)
    },
    {
      label: "Driver",
      status: getStepStatus(multiCollapsed?.driver, confirmSchedule)
    },
    {
      label: "Vehicle",
      status: getStepStatus(multiCollapsed?.vehicle, multiCollapsed?.driver)
    },
    {
      label: "Add-ons",
      status: getStepStatus(confirmAddOn, multiCollapsed?.vehicle)
    }
  ], [
    confirmSchedule,
    multiCollapsed,
    selectedLocation,
    numberOfSpots,
    selectedDates,
    selectedMultipleDrivers,
    selectedMultipleVehicles,
    confirmAddOn
  ]);

  useEffect(() => {
    if (selectedMultipleDrivers.length > 0) {
      // setMultiCollapsed((p) => ({ ...p, driver: true }));
    }
  }, [selectedMultipleDrivers]);

  useEffect(() => {
    if (selectedMultipleVehicles.length > 0) {
      // setMultiCollapsed((p) => ({ ...p, vehicle: true }));
    }
  }, [selectedMultipleVehicles]);

  // ─── BOOKING DATA ────────────────────────────────────────────
  const bookingData = useMemo(() => ({
    locationDetail: selectedLocation,
    addOnServices: addOnServicesList,
    selectedBookingType,
    selectedBookingDates: selectedDates,
    ...(bookingMode === "multiple"
      ? {
        numberOfSpots: Number(numberOfSpots),
        selectedVehicles: selectedMultipleVehicles?.filter((vcl) => vcl?._id).map((vcl) => vcl._id),
        selectedDrivers: selectedMultipleDrivers?.filter((dr) => dr?._id).map((dr) => dr._id),
      }
      : {
        selectedVehicleId: selectedVehicle?._id,
        selectedDriverId: selectedDriver?._id,
      }),
  }), [
    selectedLocation,
    selectedVehicle,
    selectedDriver,
    selectedDates,
    addOnServicesList,
    selectedBookingType,
    bookingMode, numberOfSpots,
    selectedMultipleVehicles,  // add missing dependencies
    selectedMultipleDrivers,
  ]);


  // ─── VALIDATION ──────────────────────────────────────────────
  const validateSingleBookingData = (data) => {
    if (!data?.bookings?.length) { errorAlert({ message: "Bookings data is missing or invalid" }); return false; }
    const b = data.bookings[0];
    if (!b?.parkingLocationId) { errorAlert({ message: "Parking Location ID is missing" }); return false; }
    if (!b?.vehicleId) { errorAlert({ message: "Vehicle ID is missing" }); return false; }
    if (!b?.driverId) { errorAlert({ message: "Driver ID is missing" }); return false; }
    if (!b?.bookingType) { errorAlert({ message: "Booking Type is missing" }); return false; }
    if (!b?.bookingDates?.length) { errorAlert({ message: "Booking Dates are missing or invalid" }); return false; }
    return true;
  };

  const validateMultipleBookingData = (data) => {
    if (!data?.parkingLocationId) { errorAlert({ message: "Parking Location ID is missing" }); return false; }
    if (!data?.bookingDates?.length) { errorAlert({ message: "Booking Dates are missing or invalid" }); return false; }
    if (!data?.numberOfSpots) { errorAlert({ message: "Selected slots are missing or invalid" }); return false; }
    if (!data?.numberOfSpots > 0) { errorAlert({ message: "Selected slots should be more than 0" }); return false; }
    return true;
  };

  // ─── PAY NOW ─────────────────────────────────────────────────
  const handlePayNowClick = async (isFree, selectedPayment) => {
    setLoading(true);

    const addOnServiceIds = bookingData?.addOnServices?.filter(s => s.isSelected)?.map(s => s._id);
    const bookingDates = bookingData?.selectedBookingDates || [];
    const tz = bookingData?.locationDetail?.location_timezone || "UTC";

    const convertToTZ = (ds) => new Date(new Date(ds).toLocaleString("en-US", { timeZone: tz }));
    const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const customStartTime = bookingDates.length > 0
      ? convertToTZ(bookingDates[0].startDateTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
      : "";
    const customSelectedDates = bookingDates.map(item => toYMD(convertToTZ(item.startDateTime)));

    // ================================
    // PREPARE BOOKING DATES
    // ================================
    const preparedBookingDates = isExtendParking
      ? bookingData?.selectedBookingDates?.map((date) => ({
        startDateTime: moment.utc(date.endDateTime).add(1, "second").toISOString(),
        endDateTime: date.endDateTime,
      }))
      : bookingData?.selectedBookingDates;

    const singleSlotApiData = {
      stripeCustomerId: userProfileData?.stripeCustomerId || "",
      selectedPayment,
      isWebRequest: true,
      bookings: [{
        parkingLocationId: bookingData?.locationDetail?._id,
        vehicleId: bookingData?.selectedVehicleId,
        bookingType: bookingData?.selectedBookingType,
        driverId: bookingData?.selectedDriverId,
        addOnServiceIds,
        bookingDates: preparedBookingDates,
        startTime: customStartTime,
        selectedDates: customSelectedDates,
        ...(isExtendParking && { previousBookingId: extendBookingData?.bookingId }),
      }],
    };

    const multiSlotApiData = {
      stripeCustomerId: userProfileData?.stripeCustomerId || "",
      selectedPayment,
      isWebRequest: true,
      numberOfSpots: bookingData?.numberOfSpots,
      parkingLocationId: bookingData?.locationDetail?._id,
      vehicleIds: bookingData?.selectedVehicles,
      driverIds: bookingData?.selectedDrivers,
      bookingType: bookingData?.selectedBookingType,
      startTime: customStartTime,
      selectedDates: customSelectedDates,
      addOnServiceIds,
      bookingDates: preparedBookingDates,
      ...(isExtendParking && { previousBookingId: extendBookingData?.bookingId }),
    };

    try {

      let res;
      if (bookingMode === "multiple" && validateMultipleBookingData(multiSlotApiData)) {
        res = await multipleSlotBooking(multiSlotApiData, bookingData?.locationDetail?.location_timezone);
      } else if (bookingMode === "single" && validateSingleBookingData(singleSlotApiData)) {
        res = await singleSlotBooking(singleSlotApiData, bookingData?.locationDetail?.location_timezone);
      } else {
        setLoading(false);
        return;
      }

      if (res.success) {
        setLoading(false);
        if (isFree) {
          if (res.isFreeBookingConfirm) {
            Swal.fire({
              title: "Booking Confirmed!",
              text: "Your booking has been confirmed.",
              icon: "success",
              confirmButtonText: "OK",
              customClass: {
                popup: "custom-swal-popup",
                title: "custom-swal-title",
                htmlContainer: "custom-swal-text",
                confirmButton: "custom-swal-confirm",
                cancelButton: "custom-swal-cancel",
              },
            })
              .then(() => { navigate("/", { replace: true }); localStorage.removeItem("remainingTime"); });
          } else {
            errorAlert({ message: res.message });
          }
        } else {
          selectedPayment === "STRIPE"
            ? setStripeSessionDetail(res?.data)
            : window.location.replace(res?.data?.paymentURL);
        }
      } else {
        errorAlert({ message: res.message });
        setLoading(false);
      }
    } catch (err) {
      errorAlert({ message: err?.response?.data?.message || err?.message || "Something went wrong during booking." });
    } finally {
      setLoading(false);
    }
  };

  // ─── STRIPE MODAL ────────────────────────────────────────────
  useEffect(() => {
    if (stripeSessionDetail) setShowStripeModal(true);
  }, [stripeSessionDetail]);

  const onPaymentCompleteHandler = async () => {
    setLoading(true);
    setShowStripeModal(false);
    const res = await getSessionStatus(stripeSessionDetail.id);
    if (res.success && res.status === "complete") {
      setLoading(false);
      Swal.fire({
        title: "Payment Successful!",
        text: "Your booking has been confirmed.",
        icon: "success",
        confirmButtonText: "OK",
        customClass: {
          popup: "custom-swal-popup",
          title: "custom-swal-title",
          htmlContainer: "custom-swal-text",
          confirmButton: "custom-swal-confirm",
          cancelButton: "custom-swal-cancel",
        },
      })
        .then(() => { handleCancelClick(); navigate("/"); localStorage.removeItem("remainingTime"); });
    } else {
      setLoading(false);
      Swal.fire({
        title: "Payment Incomplete",
        text: "The payment was not completed.",
        icon: "error",
        confirmButtonText: "Retry",
        customClass: {
          popup: "custom-swal-popup",
          title: "custom-swal-title",
          htmlContainer: "custom-swal-text",
          confirmButton: "custom-swal-confirm",
          cancelButton: "custom-swal-cancel",
        },
      });
    }
  };

  const handleCloseModal = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to cancel the payment? Your booking will not be confirmed.",
      icon: "warning", showCancelButton: true,
      confirmButtonText: "Yes, Cancel Payment", confirmButtonColor: "#ff6060",
      cancelButtonText: "No, Go Back",
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        htmlContainer: "custom-swal-text",
        confirmButton: "custom-swal-confirm",
        cancelButton: "custom-swal-cancel",
      },
    }).then((result) => {
      if (result.isConfirmed) { setShowStripeModal(false); handleCancelClick(); localStorage.removeItem("remainingTime"); }
    });
  };

  // ─── CANCEL ──────────────────────────────────────────────────
  const handleCancelClick = () => {
    setIsExtendParking(false);
    setSelectedDriver(null);
    setSelectedVehicle(null);
    setSelectedLocation(null);
    setSelectedDates([]);
    setAddOnServicesList([]);
    setNumberOfSpots(1);
    setConfirmSchedule(false);
    setConfirmAddOn(false);
    setSelectedBookingType("MONTHLY_SUBSCRIPTION");
    setIsMonthlySubscription(true);
    setInData({ startTime: "12:00 AM", endTime: "11:59 PM" });
    setRemainingTime(300);
    setCollapsed({ driver: true, vehicle: true, location: true, schedule: true, extras: true });
    setMultiCollapsed({ location: false, spots: false, plan: false, driver: false, vehicle: false, schedule: false, extras: false });
    localStorage.removeItem("remainingTime");
  };

  const handleTabSwitch = (mode) => {
    if (bookingMode === mode) return; // already on this tab, do nothing
    setBookingMode(mode);
    handleCancelClick();
  };

  // ─── SECTION HEADER ──────────────────────────────────────────
  // Reusable header with title, summary when collapsed, and chevron
  const SectionHeader = ({ title, summary, isCollapsed, onToggle }) => (
    <div
      className="d-flex justify-content-between align-items-center cursor-pointer"
      onClick={onToggle}
    >
      <div className="d-flex align-items-center gap-2 ">
        <strong>{title}</strong>
        {/* Show summary inline when collapsed */}
        {isCollapsed && summary && (
          <span className="text-success small fw-semibold">— {summary}</span>
        )}
      </div>
      {isCollapsed ? <ChevronDown size={18} color="#2563eb" /> : <ChevronUp size={18} color="#6b7280" />}
    </div>
  );

  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <>
      {/* ============code commented for hide single booking============== */}

      {/* <div className="rb-tabs-wrapper px-3 pt-2">
        <div className="rb-tabs d-flex">
          <div className={`rb-tab ${bookingMode === "single" ? "active" : ""}`}
            onClick={() => handleTabSwitch("single")}>
            Single Booking
          </div>
          <div className={`rb-tab ${bookingMode === "multiple" ? "active" : ""}`}
            onClick={() => handleTabSwitch("multiple")}>
            Multiple Booking
          </div>
        </div>
      </div> */}

      <div className="rb-wrapper">
        <Loader show={loading} />
        <Stepper steps={bookingMode === "single" ? steps : multipleSteps} />

        {/* ================= SINGLE BOOKING FLOW ================= */}
        {bookingMode === "single" && (
          <div className="rb-content vh-100 overflow-scroll">
            <div className="d-flex justify-content-between align-items-center">
              <h2>Reserve Parking</h2>

              {editMode && (
                <button
                  className="btn btn-success"
                  // onClick={handleUpdateBooking}
                  disabled={!selectedDriver || !selectedVehicle}
                >
                  Update Booking
                </button>
              )}
            </div>
            <p className="sub">Create a new reservation in a few simple steps.</p>

            {/* ── STEP 1: DRIVER ── */}
            <div className="card mt-3 p-3 border-primary">
              <SectionHeader
                title="Driver"
                summary={isExtendParking ? selectedDriver?.name : selectedDriver?.userName}
                isCollapsed={isExtendParking ? true : collapsed.driver}
                onToggle={() => { if (!isExtendParking) { toggleCollapse("driver"); } }}
              />
              {!isExtendParking && !collapsed.driver && (
                <DriverSection
                  selectedDriver={selectedDriver}
                  setSelectedDriver={handleDriverSelect}
                  editMode={editMode}
                />
              )}
            </div>

            {/* ── STEP 2: VEHICLE ── */}
            {(selectedDriver || selectedVehicle) && (
              <div className="card mt-3 p-3 border-primary" ref={vehicleRef}>
                <SectionHeader
                  title="Vehicle"
                  summary={selectedVehicle?.vehicleName || selectedVehicle?.licensePlate || selectedVehicle?.truck}
                  isCollapsed={isExtendParking ? true : collapsed.vehicle}
                  onToggle={() => { if (!isExtendParking) { toggleCollapse("vehicle") } }}
                />
                {!isExtendParking && !collapsed.vehicle && (
                  <VehicleSection
                    selectedVehicle={selectedVehicle}
                    setSelectedVehicle={handleVehicleSelect}
                    editMode={editMode}
                  />
                )}
              </div>
            )}

            {/* ── STEP 3: LOCATION ── */}
            {selectedVehicle && (
              <div
                className={`card mt-3 p-3 border-primary ${editMode ? "disabled-section" : ""}`}
                ref={locationRef}
              >
                <SectionHeader
                  title="Select Location"
                  summary={selectedLocation?.locationName || selectedLocation?.yard}
                  isCollapsed={isExtendParking ? true : collapsed.location}
                  onToggle={() => !editMode && toggleCollapse("location")}
                />
                {!collapsed.location && (
                  <LocationSection
                    isExtendParking={isExtendParking}
                    extendBookingData={extendBookingData}
                    onSelectLocation={handleLocationSelect}
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                  />
                )}
              </div>
            )}

            {/* ── STEP 4: SCHEDULE ── */}
            {selectedLocation && (
              <div className={`card mt-3 p-3 border-primary ${editMode ? "disabled-section" : ""}`} ref={scheduleRef}>
                <SectionHeader
                  title="Schedule"
                  summary={selectedBookingType}
                  isCollapsed={isExtendParking ? true : collapsed.schedule || collapsed.location}
                  onToggle={() => !editMode && toggleCollapse("schedule")}
                />
                {collapsed.schedule && (
                  <ScheduleSection
                    isExtendParking={isExtendParking}
                    extendBookingData={extendBookingData}
                    inData={inData}
                    setInData={setInData}
                    setConfirm={setConfirmSchedule}
                    selectedDates={selectedDates}
                    setSelectedDates={setSelectedDates}
                    locationDetail={selectedLocation}
                    selectedBookingType={selectedBookingType}
                    setSelectedBookingType={setSelectedBookingType}
                    isMonthlySubscription={isMonthlySubscription}
                    setIsMonthlySubscription={setIsMonthlySubscription}
                  />
                )}
              </div>
            )}

            {/* ── STEP 5: EXTRAS ── */}
            {selectedLocation && confirmSchedule && (
              <div className={`card mt-3 p-3 border-primary ${editMode ? "disabled-section" : ""}`} ref={extrasRef}>
                <SectionHeader
                  title="Add-on Services"
                  summary={
                    addOnServicesList.filter(s => s.isSelected).length > 0
                      ? `${addOnServicesList.filter(s => s.isSelected).length} selected`
                      : null
                  }
                  isCollapsed={collapsed.extras}
                  onToggle={() => !editMode && toggleCollapse("extras")}
                />
                {!collapsed.extras && (
                  <ExtrasSection
                    locationDetail={selectedLocation}
                    bookingType={isMonthlySubscription ? "MONTHLY_SUBSCRIPTION" : selectedBookingType}
                    addOnServicesList={addOnServicesList}
                    setConfirm={setConfirmAddOn}
                    setAddOnServicesList={setAddOnServicesList}
                  />
                )}
              </div>
            )}

          </div>
        )}

        {/* ================= MULTIPLE BOOKING FLOW ================= */}
        {bookingMode === "multiple" && (
          <div className="rb-content  ">
           <h2>Reserve Parking</h2>

            {/* STEP 1 : LOCATION SELECTION */}
            <div className="card mt-3 p-3 border-primary">
              <SectionHeader
                title="Location"
                summary={selectedLocation?.locationName}
                isCollapsed={multiCollapsed.location}
                onToggle={() =>
                  setMultiCollapsed(p => ({ ...p, location: !p.location }))
                }
              />

              {!multiCollapsed.location && (
                <LocationSection
                  selectedLocation={selectedLocation}
                  setSelectedLocation={setSelectedLocation}
                  // onSelectLocation={(loc) => {
                  //   setSelectedLocation(loc);
                  //   setMultiCollapsed((p) => ({ ...p, location: true }));
                  // }}
                  onSelectLocation={handleLocationSelect}
                />
              )}
            </div>



            {/* STEP 2 : NUMBER OF SPOTS */}
            {multiCollapsed.location && (
              <div className="card mt-3 p-3 border-primary" ref={spotsRef}>
                <SectionHeader
                  title="Number of Spots"
                  summary={numberOfSpots > 0 ? `${numberOfSpots} spots` : null}
                  isCollapsed={multiCollapsed.spots}
                  onToggle={() =>
                    setMultiCollapsed(p => ({ ...p, spots: !p.spots }))
                  }
                />

                {!multiCollapsed.spots && (
                  <>
                    <InputField
                      type="number"
                      className="form-control my-3"
                      value={numberOfSpots}
                      min={1}
                      maxLength={2}
                      onChange={(e) => setNumberOfSpots(e.target.value)}
                      onKeydown={(e) => {
                        if (e.key === "Enter" && e.target.value > 0) {
                          setMultiCollapsed((p) => ({ ...p, spots: true }));
                        }
                      }}
                    />
                    <Button
                      name="confirm-spots"
                      onClick={() => { setConfirmSpots(true);setMultiCollapsed((p) => ({ ...p, spots: true }));scrollToRef(scheduleRef); }}
                      variant="primary ms-auto"
                      className="fw-bold"
                      size="sm"
                    >Confirm</Button>
                  </>
                )}
              </div>
            )}

            {/* STEP # : SCHEDULE */}
            {multiCollapsed.spots && (
              <div className="card mt-3 p-3 border-primary" ref={scheduleRef}>
                <SectionHeader
                  title="Schedule"
                  summary={selectedBookingType}
                  isCollapsed={multiCollapsed.schedule}
                  onToggle={() =>
                    setMultiCollapsed(p => ({ ...p, schedule: !p.schedule }))
                  }
                />

                {!multiCollapsed.schedule && (
                  <ScheduleSection
                    setConfirm={(val) => {
                      setConfirmSchedule(val);
                      // mark schedule complete
                      setMultiCollapsed((p) => ({ ...p, schedule: true }));
                      scrollToRef(driverRef);
                    }}
                    inData={inData}
                    setInData={setInData}
                    selectedDates={selectedDates}
                    setSelectedDates={setSelectedDates}
                    locationDetail={selectedLocation}
                    selectedBookingType={selectedBookingType}
                    setSelectedBookingType={setSelectedBookingType}
                    isMonthlySubscription={isMonthlySubscription}
                    setIsMonthlySubscription={setIsMonthlySubscription}
                  />
                )}
              </div>
            )}



            {/* STEP 4 : ASSIGN DRIVER (OPTIONAL) */}
            {multiCollapsed.schedule && (
              <div className="card mt-3 p-3 border-primary" ref={driverRef}>
                <SectionHeader
                  title="Assign Drivers (Optional)"
                  summary={
                    selectedMultipleDrivers.length > 0
                      ? `${selectedMultipleDrivers.length} selected`
                      : null
                  }
                  isCollapsed={multiCollapsed.driver}
                  onToggle={() =>
                    setMultiCollapsed(p => ({ ...p, driver: !p.driver }))
                  }
                />

                {!multiCollapsed.driver && (
                  <>
                    <DriverSection
                      mode="multiple"
                      selectedMultipleDrivers={selectedMultipleDrivers}
                      setMultipleDrivers={setMultipleDrivers}
                    />
                    <div className="text-end mt-3">
                      <Button
                        className="btn btn-primary me-2 px-3"
                        size="sm"
                        onClick={() => {
                          setMultipleDrivers([]);
                          setMultiCollapsed(p => ({
                            ...p,
                            driver: true,
                            vehicle: false
                          }));
                          scrollToRef(vehicleRef);
                        }}
                      >
                        skip
                      </Button>
                      <Button
                        className="btn btn-primary"
                        size="sm"
                        onClick={() => {
                          setMultiCollapsed(p => ({
                            ...p,
                            driver: true,
                            vehicle: false
                          }));
                          scrollToRef(vehicleRef);
                        }}
                      >
                        Confirm Drivers
                      </Button>
                    </div>
                  </>
                )}

              </div>
            )}


            {/* STEP 5 : ASSIGN VEHICLES (OPTIONAL) */}
            {multiCollapsed.driver && (
              <div className="card mt-3 p-3 border-primary" ref={vehicleRef}>

                <SectionHeader
                  title="Assign Vehicles (Optional)"
                  summary={
                    selectedMultipleVehicles.length > 0
                      ? `${selectedMultipleVehicles.length} selected`
                      : null
                  }
                  isCollapsed={multiCollapsed.vehicle}
                  onToggle={() =>
                    setMultiCollapsed(p => ({ ...p, vehicle: !p.vehicle }))
                  }
                />

                {!multiCollapsed.vehicle && (
                  <>
                    <VehicleSection
                      mode="multiple"
                      selectedMultipleVehicles={selectedMultipleVehicles}
                      setMultipleVehicles={setMultipleVehicles}
                    />

                    <div className="text-end mt-3">
                      <Button
                        className="btn btn-primary me-2 px-3"
                        size="sm"
                        onClick={() => {
                          setMultipleVehicles([]);
                          setMultiCollapsed(p => ({
                            ...p,
                            vehicle: true,
                            extras: false
                          }));
                          scrollToRef(extrasRef);
                        }}
                      >
                        skip
                      </Button>
                      <Button
                        className="btn btn-primary"
                        size="sm"
                        onClick={() => {
                          setMultiCollapsed(p => ({
                            ...p,
                            vehicle: true,
                            extras: false
                          }));
                          scrollToRef(extrasRef);
                        }}
                      >
                        Confirm Vehicles
                      </Button>
                    </div>
                  </>
                )}

              </div>
            )}



            {/* STEP 6 :ADD ON SERVICE */}
            {multiCollapsed.vehicle && (
              <div className="card mt-3 p-3 border-primary" ref={extrasRef}>
                <SectionHeader
                  title="Add-ons"
                  summary={
                    addOnServicesList.filter(s => s.isSelected).length > 0
                      ? `${addOnServicesList.filter(s => s.isSelected).length} selected`
                      : null
                  }
                  isCollapsed={multiCollapsed.extras}
                  onToggle={() =>
                    setMultiCollapsed(p => ({ ...p, extras: !p.extras }))
                  }
                />

                {!multiCollapsed.extras && (
                  <ExtrasSection
                    locationDetail={selectedLocation}
                    bookingType={selectedBookingType}
                    addOnServicesList={addOnServicesList}
                    setConfirm={(val) => {
                      setConfirmAddOn(val);
                      setMultiCollapsed((p) => ({ ...p, extras: true }));
                      scrollToRef(summaryRef);
                    }}
                    setAddOnServicesList={setAddOnServicesList}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* SUMMARY PANEL */}
        <div className={confirmAddOn ? "" : "rb-summary-panel"} ref={summaryRef}>
        <SummaryPanel
          isExtendParking={isExtendParking}
          driver={
            bookingMode === "multiple"
              ? selectedMultipleDrivers?.[0]
              : selectedDriver
          }
          vehicle={
            bookingMode === "multiple"
              ? selectedMultipleVehicles
              : selectedVehicle
          }
          location={selectedLocation}
          bookingDates={selectedDates}
          addOnServices={addOnServicesList}
          selectedPayment={selectedPayment}
          setSelectedPayment={setSelectedPayment}
          bookingType={isMonthlySubscription ? "MONTHLY_SUBSCRIPTION" : selectedBookingType}
          handlePayNowClick={handlePayNowClick}
          handleCancelReservation={handleCancelClick}
          drivers={selectedMultipleDrivers}
          vehicles={selectedMultipleVehicles}
          numberOfSpots={numberOfSpots}
          bookingMode={bookingMode}
          />
        </div>

      </div>

      {showStripeModal && (
        <PaymentModal
          show={showStripeModal}
          onHide={handleCloseModal}
          close={handleCloseModal}
          sessionDetail={stripeSessionDetail}
          onPaymentCompleteHandler={onPaymentCompleteHandler}
        />
      )}
    </>
  );
}